import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Dialog, Menu, Transition } from '@headlessui/react';
import apiService from '../../services/ApiService';
import snakeToPascal from '../../tools';
import { ILibraryItem, IGlobalLibraryBlock } from '../../types/LibraryItem';
import TailwindModal from '../../components/Tailwind/TailwindModal';
import motionify from '../../components/Motion';
import { FieldByKindAndType } from '../../components/ModelDataPanel';
import FieldsInitializer from '../../components/ModelDataFields/Input/Initializer';
import LibraryBrowserModal from '../../components/LibraryBrowserModal';
import {
    TYPE_META,
    FALLBACK_COLOR,
    getTypeLabel,
    getTypeColor,
    sortTypes,
} from '../../components/Library/typeMeta';
import { useEditorStore } from '../../shared/stores/useEditorStore';
import { cn } from '../../lib/utils';

// Picker entries are loaded from `GET /library/creatable-types` — every input
// type that appears as a child of some other type with `auto_create=False`,
// since the library scenario root bypasses parent-type rules.
interface CreatableType {
    type: string;
    default_name: string | null;
}

function formatRelative(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = dayjs(iso);
    if (!d.isValid()) return '—';
    const diffMs = Date.now() - d.valueOf();
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (diffMs < minute) return 'just now';
    if (diffMs < hour) {
        const m = Math.floor(diffMs / minute);
        return `${m} minute${m === 1 ? '' : 's'} ago`;
    }
    if (diffMs < day) {
        const h = Math.floor(diffMs / hour);
        return `${h} hour${h === 1 ? '' : 's'} ago`;
    }
    if (diffMs < 2 * day) return 'Yesterday';
    if (diffMs < 7 * day) {
        const days = Math.floor(diffMs / day);
        return `${days} days ago`;
    }
    return d.format('MMM DD');
}

type Source = 'global' | 'personal';

const SOURCE_META: Record<Source, { heading: string; subheading: string; emptyAll: string }> = {
    global: {
        heading: 'Peerception blocks',
        subheading: 'Shipped by Peerception',
        emptyAll: 'No Peerception blocks yet.',
    },
    personal: {
        heading: 'My library',
        subheading: 'Saved by you · private to your account',
        emptyAll: 'Save building blocks from your models using the context menu.',
    },
};

interface LibraryRow {
    uid: string;
    kind: Source;
    refId: number | string;
    name: string;
    description: string | null;
    root_type: string;
    include_children: boolean;
    created_at?: string;
    updated_at?: string;
    // Personal rows only: enough state to navigate into the FME for editing.
    library_model_id?: number;
    library_scenario_id?: number;
    model_data_id?: number;
}

function personalToRow(item: ILibraryItem): LibraryRow {
    return {
        uid: `p:${item.id}`,
        kind: 'personal',
        refId: item.id,
        name: item.name,
        description: item.description,
        root_type: item.root_type,
        include_children: true,
        created_at: item.created_at,
        updated_at: item.updated_at,
        library_model_id: item.library_model_id,
        library_scenario_id: item.library_scenario_id,
        model_data_id: item.model_data_id,
    };
}

function globalToRow(block: IGlobalLibraryBlock): LibraryRow {
    return {
        uid: `g:${block.slug}`,
        kind: 'global',
        refId: block.slug,
        name: block.name,
        description: block.description,
        root_type: block.root_type,
        include_children: block.include_children,
    };
}

interface CreateBlockModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (type: string, name: string, description: string | null) => Promise<void>;
}

function CreateBlockModal({ isOpen, onClose, onCreate }: CreateBlockModalProps) {
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [types, setTypes] = useState<CreatableType[]>([]);
    const [typesLoading, setTypesLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedType(null);
            setName('');
            setDescription('');
            setSubmitting(false);
            setSearch('');
        }
    }, [isOpen]);

    // Fetch the full list of user-creatable types from the backend the first
    // time the modal opens (cached for the session afterwards).
    useEffect(() => {
        if (!isOpen || types.length > 0 || typesLoading) return;
        setTypesLoading(true);
        apiService.getLibraryCreatableTypes().then(async response => {
            if (response && response.ok) {
                setTypes(await response.json());
            }
            setTypesLoading(false);
        }).catch(() => setTypesLoading(false));
    }, [isOpen, types.length, typesLoading]);

    const selected = types.find(t => t.type === selectedType);
    const selectedDefaultName = selected?.default_name || (selected ? snakeToPascal(selected.type) : '');

    const handleSubmit = async () => {
        if (!selected) return;
        const finalName = name.trim() || selectedDefaultName;
        const finalDescription = description.trim() || null;
        setSubmitting(true);
        try {
            await onCreate(selected.type, finalName, finalDescription);
        } finally {
            setSubmitting(false);
        }
    };

    // Sort by TYPE_META order when known (so common types stay near the top),
    // alpha by label otherwise. Filter by search.
    const filteredTypes = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = types.filter(t => {
            if (q === '') return true;
            const label = getTypeLabel(t.type).toLowerCase();
            return t.type.toLowerCase().includes(q) || label.includes(q);
        });
        return matches.sort((a, b) => {
            const oa = TYPE_META[a.type]?.order ?? 999;
            const ob = TYPE_META[b.type]?.order ?? 999;
            if (oa !== ob) return oa - ob;
            return getTypeLabel(a.type).localeCompare(getTypeLabel(b.type));
        });
    }, [types, search]);

    return (
        <TailwindModal isOpen={isOpen} onClose={submitting ? () => {} : onClose} title="New library block" size="lg">
            <div className="space-y-4">
                <div>
                    <label className="block text-[12px] font-semibold text-slate-500 uppercase tracking-[0.06em] mb-2">
                        Block type
                    </label>
                    <div className="relative mb-2">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px] pointer-events-none">
                            search
                        </span>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search block types..."
                            disabled={submitting}
                            className="w-full h-9 pl-9 pr-3 border border-gray-200 rounded-md bg-white text-[13px] outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto">
                        {typesLoading ? (
                            <div className="col-span-2 text-center py-8 text-[12.5px] text-slate-400 italic">
                                Loading block types...
                            </div>
                        ) : filteredTypes.length === 0 ? (
                            <div className="col-span-2 text-center py-8 text-[12.5px] text-slate-400 italic">
                                {types.length === 0 ? 'No types available.' : 'No types match your search.'}
                            </div>
                        ) : filteredTypes.map(opt => {
                            const active = selectedType === opt.type;
                            const color = TYPE_META[opt.type]?.color ?? FALLBACK_COLOR;
                            return (
                                <button
                                    key={opt.type}
                                    type="button"
                                    onClick={() => setSelectedType(opt.type)}
                                    className={cn(
                                        'flex items-center gap-2 px-3 py-2.5 rounded-md text-left border transition-colors disabled:opacity-50',
                                        active ? 'border-black bg-slate-50' : 'border-gray-200 hover:bg-slate-50',
                                    )}
                                    disabled={submitting}
                                >
                                    <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                                    <span className="text-[13.5px] font-medium text-slate-900 truncate">
                                        {getTypeLabel(opt.type)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        placeholder={selected ? selectedDefaultName : 'Pick a type first'}
                        disabled={!selected || submitting}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        placeholder="Describe this building block..."
                        disabled={submitting}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!selected || submitting}
                        className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white" />
                                Creating...
                            </>
                        ) : (
                            'Create & Edit'
                        )}
                    </button>
                </div>
            </div>
        </TailwindModal>
    );
}

interface DeleteConfirmModalProps {
    isOpen: boolean;
    itemName: string;
    isGlobal: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

function DeleteConfirmModal({ isOpen, itemName, isGlobal, onClose, onConfirm }: DeleteConfirmModalProps) {
    return (
        <TailwindModal isOpen={isOpen} onClose={onClose} title="Delete this block?" size="sm">
            <div className="space-y-5">
                <p className="text-sm text-slate-500 leading-relaxed">
                    <strong className="text-slate-900">"{itemName}"</strong>
                    {isGlobal
                        ? ' will be removed from the Peerception blocks directory (the JSON file will be deleted on disk). Models that already use this block won’t be affected.'
                        : ' will be removed from your library. Models that already use this block won’t be affected.'}
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </TailwindModal>
    );
}

type LibraryEditTarget =
    | {
        kind: 'personal';
        libraryItemId: number;
        libraryScenarioId: number;
        modelDataId: number;
        // Initial description (rendered immediately so the modal doesn't
        // flicker; then edits go through PUT /library/{id}).
        description: string | null;
    }
    | { kind: 'global'; slug: string };

interface LibraryEditModalProps {
    isOpen: boolean;
    target: LibraryEditTarget | null;
    onClose: () => void;
}

/**
 * Add menu styled like the FME's `AddButtonMenu`: a `+` button that opens a
 * dropdown listing the parent's allowed children types plus a "From Library…"
 * entry that opens the standard `LibraryBrowserModal`. Library-specific so we
 * can call our own refresh path instead of patching the FME tree cache.
 */
function LibraryAddMenu({
    parent,
    libraryScenarioId,
    onChildCreated,
    onOpen,
}: {
    parent: any | null;
    libraryScenarioId: number;
    onChildCreated: (createdInputDict: Record<string, any>, parentId: number) => void;
    // Called right before the menu opens — lets the caller lazy-load the
    // parent's full model_data (needed for `possible_childrens`).
    onOpen?: () => void | Promise<any>;
}) {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [dropdownPos, setDropdownPos] = useState<React.CSSProperties>({});
    const [submitting, setSubmitting] = useState(false);
    const [browserOpen, setBrowserOpen] = useState(false);

    const possibleChildren: any[] = parent?.possible_childrens ?? [];

    const handleButtonClick = () => {
        if (onOpen) onOpen();
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const estimated = (possibleChildren.length + 1) * 40 + 8;
        setDropdownPos(spaceBelow >= estimated
            ? { position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 1600 }
            : { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left, zIndex: 1600 });
    };

    const handlePickType = async (type: string, customName: boolean) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const response = await apiService.postModelData(
                customName ? '' : snakeToPascal(type),
                type,
                libraryScenarioId,
                parent.id,
                0,
            );
            if (response && response.ok) {
                const data = await response.json();
                onChildCreated(data['input'] || {}, parent.id);
            }
        } catch (e) {
            console.error('Failed to add child', e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleLibraryApplied = (newItems: { input: Record<string, any>; output: Record<string, any> }) => {
        if (parent) onChildCreated(newItems.input || {}, parent.id);
    };

    const buttonDisabled = submitting || (parent != null && possibleChildren.length === 0);

    return (
        <>
            <Menu as="div" className="relative inline-block">
                {({ open }) => (
                    <>
                        <Menu.Button
                            ref={buttonRef}
                            className="inline-flex items-center gap-1 p-1 rounded text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
                            onClick={handleButtonClick}
                            disabled={buttonDisabled}
                            title={
                                !parent ? 'Add child'
                                : possibleChildren.length === 0 ? 'No child types allowed here'
                                : 'Add child'
                            }
                        >
                            <span className="material-symbols-outlined text-[20px]">add</span>
                        </Menu.Button>
                        <Transition
                            show={!!open}
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items
                                className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden bg-white min-w-[220px]"
                                style={dropdownPos}
                            >
                                <div className="p-0">
                                    {!parent && (
                                        <div className="px-3 py-2 text-[12px] text-slate-400 italic">
                                            Loading…
                                        </div>
                                    )}
                                    {possibleChildren.map(elem => (
                                        <Menu.Item key={elem.type}>
                                            {({ active }) => (
                                                <button
                                                    title={elem.instantiable === false ? 'Upgrade your plan' : ''}
                                                    onClick={() => {
                                                        if (elem.instantiable !== false) {
                                                            handlePickType(elem.type, !!elem.custom_name);
                                                        }
                                                    }}
                                                    className={cn(
                                                        'flex items-center w-full px-3 py-2 text-sm text-left transition-colors',
                                                        elem.instantiable === false && 'text-[#BBBBBB]',
                                                    )}
                                                    style={{
                                                        boxShadow: active ? 'inset 0 0 0 999px rgba(0,0,0,0.07)' : 'none',
                                                    }}
                                                >
                                                    {elem.instantiable === false ? (
                                                        <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">lock</span>
                                                    ) : (
                                                        <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">add</span>
                                                    )}
                                                    {elem.default_name || snakeToPascal(elem.type)}
                                                </button>
                                            )}
                                        </Menu.Item>
                                    ))}
                                    {process.env.REACT_APP_LIBRARY_ENABLED === 'true' && (
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    onClick={() => setBrowserOpen(true)}
                                                    className="flex items-center w-full px-3 py-2 text-sm text-left transition-colors border-t border-black/10"
                                                    style={{
                                                        boxShadow: active ? 'inset 0 0 0 999px rgba(0,0,0,0.07)' : 'none',
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">library_add</span>
                                                    From Library...
                                                </button>
                                            )}
                                        </Menu.Item>
                                    )}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </>
                )}
            </Menu>
            {parent && (
                <LibraryBrowserModal
                    isOpen={browserOpen}
                    onClose={() => setBrowserOpen(false)}
                    parentModelDataId={parent.id}
                    scenarioId={libraryScenarioId}
                    onApplied={handleLibraryApplied}
                />
            )}
        </>
    );
}

const FULL_WRITE_RIGHTS = {
    user_id: 0,
    right_to_share: true,
    right_to_read: true,
    right_to_write: true,
    right_to_duplicate: true,
};

const NO_RIGHTS = {
    user_id: 0,
    right_to_share: false,
    right_to_read: false,
    right_to_write: false,
    right_to_duplicate: false,
};

interface NodeSummary {
    id: number;
    name: string;
    type: string;
    position: number;
    has_children?: boolean;
}

/**
 * Inline editor for personal library blocks. Built as a split-pane modal:
 * left = lazily-loaded subtree of the library item, right = field editor for
 * the selected node. The tree is the natural place for navigation, expansion
 * and the "add child" action — no floating toolbar.
 */
function LibraryEditModal({ isOpen, target, onClose }: LibraryEditModalProps) {
    const setStoreScenarioId = useEditorStore(s => s.setScenarioId);
    const setUserRights = useEditorStore(s => s.setUserRights);
    const previousScenarioIdRef = useRef<number | null>(null);
    const previousRightsRef = useRef<typeof FULL_WRITE_RIGHTS | null>(null);

    const isGlobal = target?.kind === 'global';
    const readOnly = isGlobal;
    const libraryScenarioId = target?.kind === 'personal' ? target.libraryScenarioId : 0;

    // Selected node = which one's fields are shown on the right.
    const [selectedId, setSelectedId] = useState<number>(0);
    // Loaded item (model_data with fields) for the selected node.
    const [item, setItem] = useState<any | null>(null);
    const [itemLoading, setItemLoading] = useState(false);
    // Identity of the LIBRARY ITEM root — used for the modal header. Stays
    // pinned to the saved block even when the user drills into children.
    const [rootMeta, setRootMeta] = useState<{ name: string; type: string } | null>(null);
    // Editable description of the LIBRARY ITEM (personal mode). Saved via
    // PUT /library/{id} on blur. Globals show their description read-only.
    const [description, setDescription] = useState<string>('');
    const lastSavedDescriptionRef = useRef<string>('');

    // Tree state — keyed by parent id.
    const [childrenByParent, setChildrenByParent] = useState<Record<number, NodeSummary[]>>({});
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [loadingChildren, setLoadingChildren] = useState<Set<number>>(new Set());

    // Inline rename state (personal only).
    const [renamingId, setRenamingId] = useState<number | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const renameSubmittedRef = useRef(false);

    // Global-mode in-memory data: the JSON payload from /library/global/{slug}
    // and a synthesised id → node lookup over its `data.input` dict.
    const [globalBlock, setGlobalBlock] = useState<any | null>(null);
    const globalIdMapRef = useRef<Record<number, any>>({});
    const globalRootIdRef = useRef<number | null>(null);

    // Editor store handshake: scenarioId + write rights for the lifetime of
    // the modal. Read-only / global mode keeps right_to_write=false so every
    // FieldByKindAndType component renders disabled.
    useEffect(() => {
        if (!isOpen || !target) return undefined;
        previousScenarioIdRef.current = useEditorStore.getState().scenarioId;
        previousRightsRef.current = useEditorStore.getState().userRights;
        setStoreScenarioId(libraryScenarioId);
        setUserRights(readOnly ? NO_RIGHTS : FULL_WRITE_RIGHTS);
        return () => {
            setStoreScenarioId(previousScenarioIdRef.current);
            setUserRights(previousRightsRef.current ?? NO_RIGHTS);
        };
    }, [isOpen, libraryScenarioId, readOnly, target]);

    // Reset state on every (re)open.
    useEffect(() => {
        if (!isOpen || !target) return;
        setItem(null);
        setRootMeta(null);
        setChildrenByParent({});
        setExpanded(new Set());
        setRenamingId(null);
        setRenameValue('');
        if (target.kind === 'personal') {
            setSelectedId(target.modelDataId);
            setExpanded(new Set([target.modelDataId]));
            const initialDesc = target.description ?? '';
            setDescription(initialDesc);
            lastSavedDescriptionRef.current = initialDesc;
            globalIdMapRef.current = {};
            globalRootIdRef.current = null;
            setGlobalBlock(null);
        } else {
            // Global: selectedId / expanded get set once the JSON arrives.
            setSelectedId(0);
            setDescription('');
            lastSavedDescriptionRef.current = '';
            setGlobalBlock(null);
            globalIdMapRef.current = {};
            globalRootIdRef.current = null;
        }
    }, [isOpen, target]);

    // Personal mode: fetch the active model_data via the API.
    useEffect(() => {
        if (!isOpen || !target || target.kind !== 'personal' || !selectedId) return undefined;
        let cancelled = false;
        setItemLoading(true);
        setItem(null);
        apiService.getModelData(selectedId).then(async response => {
            if (cancelled) return;
            if (response && response.ok) {
                const data = await response.json();
                if (data?.fields) FieldsInitializer(data.fields);
                setItem(data);
                // Pin the header to the LIBRARY ITEM root, not whatever node
                // the user has drilled into. The first load is always for the
                // root because `selectedId` is initialised to target.modelDataId.
                if (target.kind === 'personal' && data?.id === target.modelDataId) {
                    setRootMeta({ name: data.name, type: data.type });
                }
            }
            setItemLoading(false);
        }).catch(() => {
            if (!cancelled) setItemLoading(false);
        });
        return () => { cancelled = true; };
    }, [isOpen, selectedId, target]);

    // Global mode: fetch the JSON once, walk it, set initial selection.
    useEffect(() => {
        if (!isOpen || !target || target.kind !== 'global') return undefined;
        let cancelled = false;
        setItemLoading(true);
        apiService.getGlobalLibraryBlock(target.slug).then(async response => {
            if (cancelled) return;
            if (response && response.ok) {
                const blob = await response.json();
                const inputDict: Record<string, any> = blob?.data?.input || {};
                const idMap: Record<number, any> = {};
                for (const node of Object.values(inputDict) as any[]) {
                    if (node?.id != null) idMap[node.id] = node;
                }
                // Root = the node whose parent_id is not in our id map (the
                // library item itself; ancestors above it weren't saved).
                let rootNode: any = null;
                for (const node of Object.values(inputDict) as any[]) {
                    if (node.parent_id == null || !idMap[node.parent_id]) {
                        rootNode = node;
                        break;
                    }
                }
                if (!rootNode) {
                    rootNode = (Object.values(inputDict) as any[])[0] ?? null;
                }
                // Populate childrenByParent for the entire tree up-front (no
                // lazy loading — everything is already in memory).
                // First pass: collect every node id that appears as a parent so
                // we can mark each child with `has_children` (drives chevron
                // visibility in LibraryTreeNode).
                const parentIds = new Set<number>();
                for (const node of Object.values(inputDict) as any[]) {
                    if (node.parent_id != null && idMap[node.parent_id]) {
                        parentIds.add(node.parent_id);
                    }
                }
                const cByP: Record<number, NodeSummary[]> = {};
                for (const node of Object.values(inputDict) as any[]) {
                    const pid = node.parent_id;
                    if (pid != null && idMap[pid]) {
                        if (!cByP[pid]) cByP[pid] = [];
                        cByP[pid].push({
                            id: node.id,
                            name: node.name,
                            type: node.type,
                            position: node.position ?? 0,
                            has_children: parentIds.has(node.id),
                        });
                    }
                }
                for (const list of Object.values(cByP)) {
                    list.sort((a, b) => a.position - b.position);
                }

                globalIdMapRef.current = idMap;
                globalRootIdRef.current = rootNode?.id ?? null;
                setGlobalBlock(blob);
                setChildrenByParent(cByP);
                // Pin the header to the saved Peerception block (its top-level
                // name + root_type) so drilling into children doesn't change it.
                setRootMeta({
                    name: blob?.name ?? rootNode?.name ?? '',
                    type: blob?.root_type ?? rootNode?.type ?? '',
                });
                if (rootNode?.id != null) {
                    setSelectedId(rootNode.id);
                    setExpanded(new Set([rootNode.id]));
                }
            }
            setItemLoading(false);
        }).catch(() => {
            if (!cancelled) setItemLoading(false);
        });
        return () => { cancelled = true; };
    }, [isOpen, target]);

    // Global mode: derive `item` from the in-memory map whenever selection
    // changes (clone + FieldsInitializer so we don't double-mutate the JSON).
    useEffect(() => {
        if (!isOpen || !target || target.kind !== 'global' || !selectedId) return;
        const node = globalIdMapRef.current[selectedId];
        if (!node) {
            setItem(null);
            return;
        }
        const clone = JSON.parse(JSON.stringify(node));
        if (clone?.fields) {
            FieldsInitializer(clone.fields);
            // A global block is a JSON snapshot exported from another database: its field
            // ids mean nothing here, or point at someone else's data. Flag the fields so
            // field components never query the API with them.
            Object.values(clone.fields).forEach((field: any) => { field.snapshot = true; });
        }
        setItem(clone);
    }, [isOpen, selectedId, target, globalBlock]);

    const loadChildren = async (parentId: number) => {
        if (childrenByParent[parentId] || loadingChildren.has(parentId)) return;
        setLoadingChildren(prev => new Set(prev).add(parentId));
        try {
            const response = await apiService.getModelDataChildren(parentId);
            if (response && response.ok) {
                const list = await response.json();
                const summary: NodeSummary[] = (Array.isArray(list) ? list : []).map((c: any) => ({
                    id: c.id,
                    name: c.name,
                    type: c.type,
                    position: c.position ?? 0,
                    has_children: Array.isArray(c.childrens) && c.childrens.length > 0,
                }));
                setChildrenByParent(prev => ({ ...prev, [parentId]: summary }));
            } else {
                setChildrenByParent(prev => ({ ...prev, [parentId]: [] }));
            }
        } catch {
            setChildrenByParent(prev => ({ ...prev, [parentId]: [] }));
        } finally {
            setLoadingChildren(prev => {
                const next = new Set(prev);
                next.delete(parentId);
                return next;
            });
        }
    };

    // Personal mode: auto-load children for the root once when the modal opens.
    // Global mode pre-populates childrenByParent in the JSON-load effect.
    useEffect(() => {
        if (!isOpen || !target || target.kind !== 'personal') return;
        loadChildren(target.modelDataId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, target]);

    const toggleExpanded = (id: number) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        if (!isGlobal && !childrenByParent[id]) {
            loadChildren(id);
        }
    };

    const commitDescription = async () => {
        if (!target || target.kind !== 'personal') return;
        const trimmed = description.trim();
        // Treat null/'' as equivalent — only save when the user actually
        // changed something.
        const before = lastSavedDescriptionRef.current ?? '';
        if (trimmed === before.trim()) return;
        try {
            const response = await apiService.updateLibraryItem(
                target.libraryItemId,
                undefined,
                trimmed === '' ? null : trimmed,
            );
            if (response && response.ok) {
                lastSavedDescriptionRef.current = trimmed;
            }
        } catch (e) {
            console.error('Failed to update description', e);
        }
    };

    // Wrapper around the caller's onClose that flushes any pending description
    // edit before the modal disappears (covers the case where the user closes
    // the modal without blurring the textarea).
    const handleClose = () => {
        commitDescription();
        onClose();
    };

    const startRename = (id: number, currentName: string) => {
        if (readOnly) return;
        renameSubmittedRef.current = false;
        setRenamingId(id);
        setRenameValue(currentName);
    };

    const cancelRename = () => {
        renameSubmittedRef.current = true;
        setRenamingId(null);
        setRenameValue('');
    };

    const commitRename = async () => {
        if (renameSubmittedRef.current || readOnly) return;
        renameSubmittedRef.current = true;
        const id = renamingId;
        const trimmed = renameValue.trim();
        setRenamingId(null);
        if (id == null || trimmed === '') return;

        // Find current name + position to detect no-op and preserve ordering
        // (putModelData also writes position).
        let currentName: string | null = null;
        let position = 0;
        if (item?.id === id) {
            currentName = item.name;
            position = item.position ?? 0;
        } else {
            for (const list of Object.values(childrenByParent)) {
                const found = list.find(n => n.id === id);
                if (found) {
                    currentName = found.name;
                    position = found.position;
                    break;
                }
            }
        }
        if (currentName === trimmed) return;

        try {
            const response = await apiService.putModelData(id, trimmed, position);
            if (!response || !response.ok) {
                console.error('Rename failed for model_data', id);
                return;
            }
            if (item?.id === id) {
                setItem({ ...item, name: trimmed });
            }
            // If the user renamed the library item itself, keep the header
            // (which is pinned to the root) in sync.
            if (target?.kind === 'personal' && id === target.modelDataId) {
                setRootMeta(prev => prev ? { ...prev, name: trimmed } : prev);
            }
            setChildrenByParent(prev => {
                const next: Record<number, NodeSummary[]> = {};
                for (const [pid, list] of Object.entries(prev)) {
                    next[Number(pid)] = list.map(n => n.id === id ? { ...n, name: trimmed } : n);
                }
                return next;
            });
        } catch (e) {
            console.error('Failed to rename model_data', e);
        }
    };

    const handleChildCreated = (createdInputDict: Record<string, any>, parentId: number) => {
        if (readOnly) return;
        const entries = Object.values(createdInputDict) as any[];
        const top = entries.find(e => e?.parent_id === parentId) || entries[0];

        setChildrenByParent(prev => {
            const next = { ...prev };
            delete next[parentId];
            for (const e of entries) {
                if (e?.id != null) delete next[e.id];
            }
            return next;
        });

        loadChildren(parentId);
        setExpanded(prev => new Set(prev).add(parentId));
        if (top?.id) {
            setSelectedId(top.id);
        }
    };

    if (!isOpen || !target) return null;

    const treeRootId = target.kind === 'personal' ? target.modelDataId : (globalRootIdRef.current ?? 0);

    return (
        <Transition appear show={!!isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="flex items-center justify-center min-h-full p-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-[1280px] h-[88vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
                                {/* Header */}
                                <div className="px-6 py-3.5 border-b border-slate-100 shrink-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 min-w-0">
                                            {isGlobal ? (
                                                <span className="text-[10px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded tracking-widest">
                                                    Peerception · Read-only
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-bold text-primary uppercase bg-primary/10 px-2 py-0.5 rounded tracking-widest">
                                                    Library block
                                                </span>
                                            )}
                                            <h2 className="text-[16px] font-semibold text-slate-900 truncate">
                                                {rootMeta?.name ?? 'Loading...'}
                                            </h2>
                                            {rootMeta && (
                                                <span
                                                    className="inline-block px-2 py-0.5 text-[11.5px] font-medium border rounded shrink-0"
                                                    style={{
                                                        color: getTypeColor(rootMeta.type),
                                                        borderColor: getTypeColor(rootMeta.type) + '33',
                                                        background: getTypeColor(rootMeta.type) + '0d',
                                                    }}
                                                >
                                                    {getTypeLabel(rootMeta.type)}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[22px]">close</span>
                                        </button>
                                    </div>

                                    {/* Description — editable for personal items, read-only for
                                        Peerception blocks. Saves on blur via PUT /library/{id}. */}
                                    {isGlobal ? (
                                        globalBlock?.description ? (
                                            <p className="mt-2 text-[12.5px] text-slate-500 leading-snug">
                                                {globalBlock.description}
                                            </p>
                                        ) : null
                                    ) : (
                                        <textarea
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                            onBlur={commitDescription}
                                            rows={1}
                                            placeholder="Add a description... (saved on blur)"
                                            className="mt-2 w-full text-[12.5px] text-slate-700 leading-snug bg-transparent border border-transparent rounded px-2 py-1 hover:border-slate-200 focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary outline-none resize-y placeholder:text-slate-400 placeholder:italic"
                                        />
                                    )}
                                </div>

                                {/* Body: tree | fields */}
                                <div className="flex-1 flex min-h-0">
                                    {/* Tree */}
                                    <aside className="w-[300px] shrink-0 border-r border-slate-100 bg-slate-50/40 flex flex-col min-h-0">
                                        <div className="px-4 py-2.5 border-b border-slate-100 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.06em]">
                                            Structure
                                        </div>
                                        <div className="flex-1 overflow-y-auto py-2">
                                            {treeRootId > 0 && (
                                                <LibraryTreeNode
                                                    id={treeRootId}
                                                    // The root row of the tree must reflect the LIBRARY ITEM,
                                                    // not whichever child is currently selected — `item`
                                                    // changes on selection, `rootMeta` is pinned.
                                                    name={rootMeta?.name ?? '...'}
                                                    type={rootMeta?.type ?? ''}
                                                    depth={0}
                                                    isRoot
                                                    readOnly={readOnly}
                                                    selectedId={selectedId}
                                                    expanded={expanded}
                                                    childrenByParent={childrenByParent}
                                                    loadingChildren={loadingChildren}
                                                    libraryScenarioId={libraryScenarioId}
                                                    renamingId={renamingId}
                                                    renameValue={renameValue}
                                                    onSelect={setSelectedId}
                                                    onToggleExpand={toggleExpanded}
                                                    onChildCreated={handleChildCreated}
                                                    onRenameStart={startRename}
                                                    onRenameValueChange={setRenameValue}
                                                    onRenameCommit={commitRename}
                                                    onRenameCancel={cancelRename}
                                                />
                                            )}
                                        </div>
                                    </aside>

                                    {/* Fields */}
                                    <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6">
                                        {itemLoading || !item ? (
                                            <div className="text-sm text-slate-400 py-8">Loading block…</div>
                                        ) : (
                                            <FieldsEditorPanel item={item} />
                                        )}
                                    </main>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

interface LibraryTreeNodeProps {
    id: number;
    name: string;
    type: string;
    depth: number;
    isRoot?: boolean;
    readOnly?: boolean;
    selectedId: number;
    expanded: Set<number>;
    childrenByParent: Record<number, NodeSummary[]>;
    loadingChildren: Set<number>;
    libraryScenarioId: number;
    renamingId: number | null;
    renameValue: string;
    // Hint from the parent's child list: tells this node whether it likely has
    // children before it has been expanded. `undefined` means unknown (we keep
    // the chevron until proven leaf).
    hasChildren?: boolean;
    onSelect: (id: number) => void;
    onToggleExpand: (id: number) => void;
    onChildCreated: (createdInputDict: Record<string, any>, parentId: number) => void;
    onRenameStart: (id: number, currentName: string) => void;
    onRenameValueChange: (v: string) => void;
    onRenameCommit: () => void;
    onRenameCancel: () => void;
}

function LibraryTreeNode({
    id,
    name,
    type,
    depth,
    isRoot,
    readOnly = false,
    selectedId,
    expanded,
    childrenByParent,
    loadingChildren,
    libraryScenarioId,
    renamingId,
    renameValue,
    hasChildren: hasChildrenHint,
    onSelect,
    onToggleExpand,
    onChildCreated,
    onRenameStart,
    onRenameValueChange,
    onRenameCommit,
    onRenameCancel,
}: LibraryTreeNodeProps) {
    const isSelected = selectedId === id;
    const isExpanded = expanded.has(id);
    const childList = childrenByParent[id];
    const isLoading = loadingChildren.has(id);
    const [parentItem, setParentItem] = useState<any | null>(null);

    // Show the chevron only when we know (or suspect) the node has children.
    // `childList` is authoritative once loaded; otherwise fall back to the
    // parent-provided hint, and keep showing the chevron when it's unknown
    // (root node before any expansion).
    const hasChildren = childList !== undefined
        ? childList.length > 0
        : (hasChildrenHint ?? true);

    // Lazy-load the parent's full model_data when the user wants to add a child
    // from this tree node (we need its `possible_childrens` for the menu). In
    // read-only mode we never call this — there's no add menu.
    const ensureParentLoaded = async () => {
        if (readOnly) return null;
        if (parentItem) return parentItem;
        const response = await apiService.getModelData(id);
        if (response && response.ok) {
            const data = await response.json();
            setParentItem(data);
            return data;
        }
        return null;
    };

    const handleHover = () => {
        if (!readOnly && !parentItem) ensureParentLoaded();
    };

    const indent = depth * 14;
    const isRenaming = renamingId === id;

    return (
        <div>
            <div
                className={cn(
                    'group flex items-center gap-1 pr-2 py-1 cursor-pointer transition-colors',
                    isSelected ? 'bg-primary/10' : 'hover:bg-slate-100/70',
                )}
                style={{ paddingLeft: indent + 4 }}
                onClick={() => onSelect(id)}
                onMouseEnter={handleHover}
            >
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(id);
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors shrink-0"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {isExpanded ? 'expand_more' : 'chevron_right'}
                        </span>
                    </button>
                ) : (
                    <span className="w-5 h-5 shrink-0" aria-hidden="true" />
                )}
                <span
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: getTypeColor(type) }}
                />
                {isRenaming && !readOnly ? (
                    <input
                        autoFocus
                        value={renameValue}
                        onChange={e => onRenameValueChange(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onBlur={onRenameCommit}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onRenameCommit();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onRenameCancel();
                            }
                        }}
                        className="flex-1 min-w-0 px-1.5 py-0.5 text-[13px] font-medium border border-primary rounded outline-none bg-white"
                    />
                ) : (
                    <span
                        className={cn(
                            'flex-1 min-w-0 truncate text-[13px]',
                            isSelected ? 'font-semibold text-slate-900' : 'text-slate-700',
                        )}
                        onDoubleClick={readOnly ? undefined : (e => { e.stopPropagation(); onRenameStart(id, name || ''); })}
                        title={readOnly ? undefined : 'Double-click to rename'}
                    >
                        {name || (isRoot ? '...' : snakeToPascal(type))}
                    </span>
                )}
                <span className="text-[10.5px] text-slate-400 shrink-0">
                    {snakeToPascal(type).slice(0, 14)}
                </span>
                {!isRenaming && !readOnly && (
                    <div
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => onRenameStart(id, name || '')}
                            className="w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                            title="Rename"
                        >
                            <span className="material-symbols-outlined text-[16px]">edit</span>
                        </button>
                        <LibraryAddMenu
                            parent={parentItem}
                            libraryScenarioId={libraryScenarioId}
                            onChildCreated={onChildCreated}
                            onOpen={ensureParentLoaded}
                        />
                    </div>
                )}
            </div>
            {isExpanded && (
                <div>
                    {isLoading && !childList && (
                        <div
                            className="text-[11.5px] text-slate-400 italic py-1"
                            style={{ paddingLeft: indent + 28 }}
                        >
                            Loading…
                        </div>
                    )}
                    {childList && childList.length === 0 && (
                        <div
                            className="text-[11.5px] text-slate-400 italic py-1"
                            style={{ paddingLeft: indent + 28 }}
                        >
                            No children
                        </div>
                    )}
                    {(childList || []).map(c => (
                        <LibraryTreeNode
                            key={c.id}
                            id={c.id}
                            name={c.name}
                            type={c.type}
                            depth={depth + 1}
                            readOnly={readOnly}
                            selectedId={selectedId}
                            expanded={expanded}
                            childrenByParent={childrenByParent}
                            loadingChildren={loadingChildren}
                            libraryScenarioId={libraryScenarioId}
                            renamingId={renamingId}
                            renameValue={renameValue}
                            hasChildren={c.has_children}
                            onSelect={onSelect}
                            onToggleExpand={onToggleExpand}
                            onChildCreated={onChildCreated}
                            onRenameStart={onRenameStart}
                            onRenameValueChange={onRenameValueChange}
                            onRenameCommit={onRenameCommit}
                            onRenameCancel={onRenameCancel}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function FieldsEditorPanel({ item }: { item: any }) {
    const fieldEntries = Object.entries(item.fields || {}).filter(
        ([, field]: any) => !field?.hide && field?.type !== 'cross_field',
    );

    if (fieldEntries.length === 0) {
        return <p className="text-[12.5px] text-slate-400 italic">This block has no editable fields.</p>;
    }

    return (
        <div className="space-y-5">
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-[0.06em]">
                Fields
            </h3>
            {fieldEntries.map(([rawName, field]: any) => {
                const FieldComponent = (FieldByKindAndType as any)[item.kind]?.[field.type];
                field.model_data_id = item.id;
                const prettyName = snakeToPascal(rawName);
                return (
                    <div key={rawName} className="flex flex-col gap-1.5">
                        <label className="text-[13px] font-medium text-slate-700">
                            {field.display_name || prettyName}
                        </label>
                        {FieldComponent ? (
                            <FieldComponent name={prettyName} field={field} />
                        ) : (
                            <span className="text-[11.5px] text-slate-400 italic">
                                No editor for type "{field.type}"
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function buildTypeCounts(rows: LibraryRow[]): Record<string, number> {
    const c: Record<string, number> = {};
    for (const row of rows) {
        c[row.root_type] = (c[row.root_type] || 0) + 1;
    }
    return c;
}

export default function Library() {
    const [globalRows, setGlobalRows] = useState<LibraryRow[]>([]);
    const [personalRows, setPersonalRows] = useState<LibraryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [activeSource, setActiveSource] = useState<Source>('personal');
    const [activeType, setActiveType] = useState<string>('All');
    const [renamingUid, setRenamingUid] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<LibraryRow | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [editingTarget, setEditingTarget] = useState<LibraryEditTarget | null>(null);
    const renameSubmittedRef = useRef(false);

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const [personalRes, globalRes] = await Promise.all([
                apiService.getLibraryItems(),
                apiService.getGlobalLibraryBlocks(),
            ]);
            const personal: ILibraryItem[] = personalRes && personalRes.ok ? await personalRes.json() : [];
            const globals: IGlobalLibraryBlock[] = globalRes && globalRes.ok ? await globalRes.json() : [];
            setPersonalRows(personal.map(personalToRow));
            setGlobalRows(globals.map(globalToRow));
        } catch (e) {
            console.error('Failed to fetch library items', e);
        }
        setLoading(false);
    };

    const globalTypeCounts = useMemo(() => buildTypeCounts(globalRows), [globalRows]);
    const personalTypeCounts = useMemo(() => buildTypeCounts(personalRows), [personalRows]);

    const globalSidebarTypes = useMemo(() => sortTypes(Object.keys(globalTypeCounts)), [globalTypeCounts]);
    const personalSidebarTypes = useMemo(() => sortTypes(Object.keys(personalTypeCounts)), [personalTypeCounts]);

    const sourceRows = activeSource === 'global' ? globalRows : personalRows;
    const sourceMeta = SOURCE_META[activeSource];

    useEffect(() => {
        const allowed = activeSource === 'global' ? globalSidebarTypes : personalSidebarTypes;
        if (activeType !== 'All' && !allowed.includes(activeType)) {
            setActiveType('All');
        }
    }, [activeSource, globalSidebarTypes, personalSidebarTypes, activeType]);

    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return sourceRows.filter(r =>
            (activeType === 'All' || r.root_type === activeType) &&
            (q === '' || r.name.toLowerCase().includes(q))
        );
    }, [sourceRows, activeType, query]);

    const startRename = (row: LibraryRow) => {
        if (row.kind !== 'personal') return;
        renameSubmittedRef.current = false;
        setRenamingUid(row.uid);
        setRenameValue(row.name);
    };

    const cancelRename = () => {
        renameSubmittedRef.current = true;
        setRenamingUid(null);
        setRenameValue('');
    };

    const commitRename = async () => {
        if (renameSubmittedRef.current) return;
        renameSubmittedRef.current = true;
        const uid = renamingUid;
        const trimmed = renameValue.trim();
        const original = personalRows.find(r => r.uid === uid);
        setRenamingUid(null);
        if (!uid || !original || trimmed === '' || trimmed === original.name) {
            return;
        }
        try {
            const response = await apiService.updateLibraryItem(original.refId as number, trimmed);
            if (response && response.ok) {
                setPersonalRows(prev => prev.map(r =>
                    r.uid === uid ? { ...r, name: trimmed, updated_at: new Date().toISOString() } : r
                ));
            } else {
                console.error('Rename failed for library item', original.refId);
            }
        } catch (e) {
            console.error('Failed to rename library item', e);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const response = deleteTarget.kind === 'personal'
                ? await apiService.deleteLibraryItem(deleteTarget.refId as number)
                : await apiService.deleteGlobalLibraryBlock(deleteTarget.refId as string);
            if (response && response.ok) {
                if (deleteTarget.kind === 'personal') {
                    setPersonalRows(prev => prev.filter(r => r.uid !== deleteTarget.uid));
                } else {
                    setGlobalRows(prev => prev.filter(r => r.uid !== deleteTarget.uid));
                }
            }
        } catch (e) {
            console.error('Failed to delete library item', e);
        }
        setDeleteTarget(null);
    };

    const openEditor = (target: LibraryEditTarget) => {
        setEditingTarget(target);
    };

    const closeEditor = () => {
        setEditingTarget(null);
        // Personal-mode field saves don't bubble back into our local row
        // state, so re-fetch to pick up renames or other field-driven name
        // changes. (Global mode is read-only — refetch is harmless.)
        fetchItems();
    };

    const handleRowSelect = (row: LibraryRow) => {
        if (row.kind === 'global') {
            openEditor({ kind: 'global', slug: row.refId as string });
            return;
        }
        if (row.library_scenario_id && row.model_data_id) {
            openEditor({
                kind: 'personal',
                libraryItemId: row.refId as number,
                libraryScenarioId: row.library_scenario_id,
                modelDataId: row.model_data_id,
                description: row.description,
            });
        }
    };

    const handleCreate = async (type: string, name: string, description: string | null) => {
        try {
            const response = await apiService.createEmptyLibraryItem(type, name, description);
            if (response && response.ok) {
                const created: ILibraryItem = await response.json();
                setCreateOpen(false);
                if (created.library_scenario_id && created.model_data_id) {
                    openEditor({
                        kind: 'personal',
                        libraryItemId: created.id,
                        libraryScenarioId: created.library_scenario_id,
                        modelDataId: created.model_data_id,
                        description: created.description,
                    });
                }
                // The row will appear in the list once the editor closes
                // (closeEditor refetches). For an immediate update if the user
                // doesn't open the editor, we'd refetch here; left for the
                // editor close to keep things simple.
                fetchItems();
            } else {
                console.error('Failed to create library item');
            }
        } catch (e) {
            console.error('Failed to create library item', e);
        }
    };

    const selectSource = (source: Source) => {
        setActiveSource(source);
        setActiveType('All');
    };

    return motionify(
        <div className="flex-grow bg-[#fafafa] min-h-full flex flex-col text-slate-900 font-sans">
            {/* Page header */}
            <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div className="flex-1">
                        <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                            Library
                        </h2>
                        <p className="text-sm text-slate-500 mt-2">
                            Save and reuse your building blocks across models · {personalRows.length} personal · {globalRows.length} Peerception
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex min-h-0">
                {/* Sidebar */}
                <aside className="w-64 shrink-0 border-r border-gray-200 bg-white py-5 px-3 flex flex-col gap-1 overflow-y-auto">
                    <SidebarGroup
                        source="personal"
                        label="My library"
                        totalCount={personalRows.length}
                        types={personalSidebarTypes}
                        typeCounts={personalTypeCounts}
                        activeSource={activeSource}
                        activeType={activeType}
                        onSelectSource={() => selectSource('personal')}
                        onSelectType={type => { setActiveSource('personal'); setActiveType(type); }}
                    />

                    <div className="h-2" />

                    <SidebarGroup
                        source="global"
                        label="Peerception blocks"
                        totalCount={globalRows.length}
                        types={globalSidebarTypes}
                        typeCounts={globalTypeCounts}
                        activeSource={activeSource}
                        activeType={activeType}
                        onSelectSource={() => selectSource('global')}
                        onSelectType={type => { setActiveSource('global'); setActiveType(type); }}
                    />
                </aside>

                {/* Main + drawer row */}
                <div className="flex-1 flex min-w-0 min-h-0">
                    <main className="flex-1 min-w-0 px-8 py-6 flex flex-col gap-4 overflow-y-auto">
                        {/* Section header */}
                        <div className="flex items-end justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-[15px] font-semibold text-slate-900">
                                        {sourceMeta.heading}
                                    </h3>
                                    {activeType !== 'All' && (
                                        <>
                                            <span className="text-slate-400">›</span>
                                            <span
                                                className="inline-block px-2 py-0.5 text-[11.5px] font-medium border rounded"
                                                style={{
                                                    color: getTypeColor(activeType),
                                                    borderColor: getTypeColor(activeType) + '33',
                                                    background: getTypeColor(activeType) + '0d',
                                                }}
                                            >
                                                {getTypeLabel(activeType)}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <p className="text-[12px] text-slate-500 mt-0.5">{sourceMeta.subheading}</p>
                            </div>
                        </div>

                        {/* Search row */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="relative flex-1 max-w-[480px]">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">
                                    search
                                </span>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Search library items..."
                                    className="w-full h-10 pl-10 pr-3 border border-gray-200 rounded-lg bg-white text-[13.5px] outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                                />
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <span className="text-[13px] text-slate-500">
                                    {filteredRows.length} {filteredRows.length === 1 ? 'item' : 'items'}
                                </span>
                                {/* Peerception blocks are shipped read-only — they get added to
                                    the catalog via the dev-only "Save as Peerception block"
                                    flow, never from the user's "New block" button here. */}
                                {activeSource === 'personal' && (
                                    <button
                                        type="button"
                                        onClick={() => setCreateOpen(true)}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium text-white bg-primary rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">add</span>
                                        New block
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Table — Peerception blocks are read-only and shipped, so we
                            hide the "Last edited" + "Actions" columns for them. */}
                        <div className="bg-white border border-gray-200 rounded-[10px] overflow-hidden">
                            <div className="flex items-center px-4 py-2.5 bg-slate-50 border-b border-gray-200 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.05em]">
                                <div className="flex-1 min-w-0">Name</div>
                                <div className="w-[180px]">Type</div>
                                {activeSource !== 'global' && (
                                    <>
                                        <div className="w-[140px]">Last edited</div>
                                        <div className="w-[140px] text-right">Actions</div>
                                    </>
                                )}
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center py-16">
                                    <span className="text-sm text-slate-400">Loading library...</span>
                                </div>
                            ) : filteredRows.length === 0 ? (
                                <div className="py-16 px-4 text-center text-[13.5px] text-slate-400">
                                    <div className="font-medium text-slate-600">No items found</div>
                                    <div className="mt-1">
                                        {sourceRows.length === 0
                                            ? sourceMeta.emptyAll
                                            : 'Try a different search term or category.'}
                                    </div>
                                </div>
                            ) : (
                                filteredRows.map(row => (
                                    <LibraryListRow
                                        key={row.uid}
                                        row={row}
                                        isSelected={false}
                                        isRenaming={renamingUid === row.uid}
                                        renameValue={renameValue}
                                        onSelect={() => handleRowSelect(row)}
                                        onEdit={row.kind === 'personal' && row.library_scenario_id && row.model_data_id
                                            ? () => openEditor({
                                                kind: 'personal',
                                                libraryItemId: row.refId as number,
                                                libraryScenarioId: row.library_scenario_id!,
                                                modelDataId: row.model_data_id!,
                                                description: row.description,
                                            })
                                            : undefined}
                                        onRenameValueChange={setRenameValue}
                                        onRenameStart={() => startRename(row)}
                                        onRenameCommit={commitRename}
                                        onRenameCancel={cancelRename}
                                        onDelete={() => setDeleteTarget(row)}
                                    />
                                ))
                            )}
                        </div>
                    </main>
                </div>
            </div>

            <DeleteConfirmModal
                isOpen={!!deleteTarget}
                itemName={deleteTarget?.name || ''}
                isGlobal={deleteTarget?.kind === 'global'}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            />

            <CreateBlockModal
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreate={handleCreate}
            />

            <LibraryEditModal
                isOpen={!!editingTarget}
                target={editingTarget}
                onClose={closeEditor}
            />
        </div>
    );
}

interface SidebarGroupProps {
    source: Source;
    label: string;
    totalCount: number;
    types: string[];
    typeCounts: Record<string, number>;
    activeSource: Source;
    activeType: string;
    onSelectSource: () => void;
    onSelectType: (type: string) => void;
}

function SidebarGroup({
    source,
    label,
    totalCount,
    types,
    typeCounts,
    activeSource,
    activeType,
    onSelectSource,
    onSelectType,
}: SidebarGroupProps) {
    const isActiveSource = activeSource === source;

    return (
        <div>
            <button
                type="button"
                onClick={onSelectSource}
                className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-left transition-colors text-slate-800 font-semibold hover:bg-slate-100"
            >
                <span className="text-[12px] uppercase tracking-[0.06em] truncate">{label}</span>
                <span className="text-[12px] tabular-nums shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {totalCount}
                </span>
            </button>

            <div className="mt-0.5 ml-2 pl-2 border-l border-slate-100 flex flex-col gap-0.5">
                {types.length === 0 ? (
                    <span className="px-2 py-1.5 text-[12px] text-slate-400 italic">No items yet</span>
                ) : (
                    types.map(type => {
                        const isActive = isActiveSource && activeType === type;
                        const color = getTypeColor(type);
                        return (
                            <button
                                key={type}
                                type="button"
                                onClick={() => onSelectType(type)}
                                className={
                                    'flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors ' +
                                    (isActive
                                        ? 'bg-slate-100 text-slate-900 font-medium'
                                        : 'text-slate-600 hover:bg-slate-50')
                                }
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <span
                                        className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ background: color }}
                                    />
                                    <span className="truncate">{getTypeLabel(type)}</span>
                                </span>
                                <span className="text-[11.5px] text-slate-400 tabular-nums shrink-0">
                                    {typeCounts[type] || 0}
                                </span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}

interface LibraryListRowProps {
    row: LibraryRow;
    isSelected: boolean;
    isRenaming: boolean;
    renameValue: string;
    onSelect: () => void;
    onEdit?: () => void;
    onRenameValueChange: (v: string) => void;
    onRenameStart: () => void;
    onRenameCommit: () => void;
    onRenameCancel: () => void;
    onDelete: () => void;
}

function LibraryListRow({
    row,
    isSelected,
    isRenaming,
    renameValue,
    onSelect,
    onEdit,
    onRenameValueChange,
    onRenameStart,
    onRenameCommit,
    onRenameCancel,
    onDelete,
}: LibraryListRowProps) {
    const color = getTypeColor(row.root_type);
    const label = getTypeLabel(row.root_type);
    const isGlobal = row.kind === 'global';
    const canRename = row.kind === 'personal';
    // Peerception blocks are shipped with the editor — not user-managed.
    const canDelete = row.kind === 'personal';
    const canEdit = !!onEdit;

    return (
        <div
            role="row"
            tabIndex={0}
            onClick={onSelect}
            className={
                'flex items-center px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors ' +
                (isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/60')
            }
        >
            <div className="flex-1 min-w-0 pr-4 text-[13.5px]">
                {isRenaming ? (
                    <input
                        autoFocus
                        value={renameValue}
                        onChange={e => onRenameValueChange(e.target.value)}
                        onBlur={onRenameCommit}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onRenameCommit();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onRenameCancel();
                            }
                        }}
                        className="w-[70%] px-2 py-1 text-[13.5px] font-medium border border-primary rounded outline-none"
                    />
                ) : (
                    <>
                        <span className="font-medium text-slate-900 truncate block">{row.name}</span>
                        {row.description && (
                            <span className="text-[12px] text-slate-500 truncate block mt-0.5">
                                {row.description}
                            </span>
                        )}
                    </>
                )}
            </div>
            <div className="w-[180px] pr-2">
                <span
                    className="inline-block px-2 py-0.5 text-[11.5px] font-medium border rounded"
                    style={{
                        color,
                        borderColor: color + '33',
                        background: color + '0d',
                    }}
                >
                    {label}
                </span>
            </div>
            {!isGlobal && (
                <>
                    <div className="w-[140px] text-[13.5px] text-slate-500">
                        {formatRelative(row.updated_at)}
                    </div>
                    <div className="w-[140px] text-right">
                        {canEdit && (
                            <button
                                type="button"
                                onClick={e => { e.stopPropagation(); onEdit?.(); }}
                                className="px-2 py-1 ml-1 text-[12.5px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                            >
                                Edit
                            </button>
                        )}
                        {canRename && (
                            <button
                                type="button"
                                onClick={e => { e.stopPropagation(); onRenameStart(); }}
                                className="px-2 py-1 ml-1 text-[12.5px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                            >
                                Rename
                            </button>
                        )}
                        {canDelete && (
                            <button
                                type="button"
                                onClick={e => { e.stopPropagation(); onDelete(); }}
                                className="px-2 py-1 ml-1 text-[12.5px] text-red-700 hover:bg-red-50 rounded transition-colors"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
